
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../App.tsx';
import { AppStep } from '../types.ts';
import { generateDesignSystem } from '../services/gemini.ts';
import { Loader2, Palette } from 'lucide-react';

const DesignPage: React.FC = () => {
  const ctx = useContext(AppContext);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const process = async () => {
      if (!ctx?.state.extractionData) return;
      try {
        setLoading(true);
        const design = await generateDesignSystem(ctx.state.extractionData);
        ctx.dispatch({ type: 'SET_DESIGN', payload: design });
      } catch (err: any) {
        ctx?.dispatch({ type: 'SET_ERROR', payload: err.message });
      } finally {
        setLoading(false);
      }
    };
    process();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="relative mb-6">
          <Palette className="w-16 h-16 text-fuchsia-500 animate-pulse" />
          <Loader2 className="w-16 h-16 text-fuchsia-500/20 animate-spin absolute inset-0" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Engine 2: Design Intelligence</h2>
        <p className="text-slate-400 max-w-md">Synthesizing visual aesthetics, selecting color palettes, and defining typography systems based on the structural blueprint.</p>
      </div>
    );
  }

  const design = ctx?.state.designData;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-10 text-center">
        <h2 className="text-3xl font-bold mb-4">Design Intelligence Protocol</h2>
        <p className="text-slate-400">Engine 2 has crafted a bespoke design system for your UI.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        {/* Color Palette */}
        <div className="bg-slate-900 p-8 rounded-3xl border border-slate-800">
          <h3 className="text-lg font-bold mb-6 text-fuchsia-400">Color Palette</h3>
          <div className="space-y-4">
            {Object.entries(design?.colorPalette || {}).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between p-3 rounded-xl bg-slate-950 border border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md shadow-inner border border-white/10" style={{ backgroundColor: value }} />
                  <span className="capitalize text-slate-400 text-sm">{key}</span>
                </div>
                <code className="text-xs font-mono text-fuchsia-300">{value}</code>
              </div>
            ))}
          </div>
        </div>

        {/* Typography & Effects */}
        <div className="bg-slate-900 p-8 rounded-3xl border border-slate-800">
          <h3 className="text-lg font-bold mb-6 text-fuchsia-400">Visual Standards</h3>
          <div className="space-y-6">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Font Family</p>
              <p className="text-xl font-medium">{design?.typography.fontFamily}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Corner Radius</p>
              <p className="text-xl font-medium">{design?.effects.borderRadius}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Motion Design</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {design?.effects.animations.map(anim => (
                  <span key={anim} className="px-3 py-1 rounded-full bg-slate-950 border border-slate-800 text-xs text-slate-400 capitalize">
                    {anim}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-900 p-8 rounded-3xl border border-slate-800 text-center">
        <p className="text-slate-400 mb-8 italic">
          "The second agent performs Heuristic Design Synthesis to optimize the visual flow and user accessibility."
        </p>
        <button
          onClick={() => ctx?.dispatch({ type: 'SET_STEP', payload: AppStep.GENERATION })}
          className="px-12 py-4 bg-fuchsia-600 hover:bg-fuchsia-500 text-white rounded-2xl font-bold text-lg transition-all shadow-xl shadow-fuchsia-500/20"
        >
          Initialize Engine 3: Code Generation
        </button>
      </div>
    </div>
  );
};

export default DesignPage;
